import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { WarningOctagonIcon, TrashIcon, ShieldWarningIcon, BuildingsIcon } from "@phosphor-icons/react";

const SCOPES = [
  {
    key: "transactional",
    title: "Transactional data only",
    tone: "amber",
    desc: "Wipes all day-to-day records (transactions, collections, expenses, deliveries, bank deposits, boy settlements, boy activities). Keeps users, branches, cylinder types, expense categories, customers and delivery-boy profiles.",
  },
  {
    key: "branch",
    title: "Everything inside a single branch",
    tone: "orange",
    desc: "Wipes all transactional data + customers & delivery-boy profiles that belong to the selected branch. Other branches are untouched.",
  },
  {
    key: "all",
    title: "Full wipe — everything except users & settings",
    tone: "red",
    desc: "Danger. Wipes ALL transactional data plus customers, delivery-boy profiles and the audit log across every branch. Keeps only your login users, branches, cylinder types and expense categories.",
  },
];

const TONE_ICON_COLOR = {
  red: "text-[#FF2400]",
  orange: "text-orange-600",
  amber: "text-amber-600",
};

const toneClasses = {
  amber:  "border-amber-500 bg-amber-50",
  orange: "border-orange-600 bg-orange-50",
  red:    "border-[#FF2400] bg-red-50",
};

const btnTone = {
  amber:  "bg-amber-600 hover:bg-amber-700",
  orange: "bg-orange-600 hover:bg-orange-700",
  red:    "bg-[#FF2400] hover:bg-red-700",
};

export default function DataResetPage() {
  const [scope, setScope] = useState("transactional");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches");
        setBranches(r.data || []);
      } catch (e) {
        console.warn("Failed to load branches for reset dialog", e);
      }
    })();
  }, []);

  const submit = async () => {
    if (!password) return toast.error("Enter your owner password.");
    if (confirm.trim().toUpperCase() !== "RESET") return toast.error('Type "RESET" to confirm.');
    if (scope === "branch" && !branchId) return toast.error("Select a branch to wipe.");

    const label = SCOPES.find((s) => s.key === scope)?.title;
    if (!window.confirm(`This will permanently DELETE data.\n\nScope: ${label}\n\nProceed?`)) return;

    try {
      setBusy(true);
      const r = await api.post("/admin/reset", {
        password,
        scope,
        branch_id: scope === "branch" ? branchId : null,
        confirm: "RESET",
      });
      setLastResult(r.data);
      setPassword("");
      setConfirm("");
      toast.success("Data reset completed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const current = SCOPES.find((s) => s.key === scope);

  return (
    <div className="space-y-8 max-w-4xl" data-testid="data-reset-page">
      <div className="border-b border-slate-200 pb-6">
        <div className="overline text-[#FF2400] flex items-center gap-2">
          <ShieldWarningIcon size={14} weight="fill" /> Danger zone · Owner only
        </div>
        <h1 className="font-display text-4xl font-black mt-2">Data Reset</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Permanently deletes historical records so you can restart bookkeeping with a clean slate.
          Users, branches, cylinder types and expense categories are always kept so you can log back in.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setScope(s.key)}
            data-testid={`scope-${s.key}`}
            className={`text-left border-2 p-4 transition-colors ${
              scope === s.key
                ? toneClasses[s.tone]
                : "border-slate-200 bg-white hover:border-slate-400"
            }`}
          >
            <div className="flex items-center gap-2">
              <WarningOctagonIcon size={16} weight="fill" className={TONE_ICON_COLOR[s.tone] || "text-slate-600"} />
              <div className="font-semibold text-sm">{s.title}</div>
            </div>
            <div className="text-xs text-slate-600 mt-2 leading-relaxed">{s.desc}</div>
          </button>
        ))}
      </div>

      {scope === "branch" && (
        <div>
          <Label className="overline">Select branch to wipe</Label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            data-testid="branch-select"
            className="mt-1 block w-full max-w-sm border border-slate-300 rounded-none px-3 py-2 text-sm bg-white"
          >
            <option value="">— pick a branch —</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className={`border-2 p-5 ${toneClasses[current.tone]}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="overline">Your owner password</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none bg-white"
              placeholder="••••••••"
              data-testid="reset-password"
            />
          </div>
          <div>
            <Label className="overline">Type RESET to confirm</Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="rounded-none bg-white font-mono-num tracking-widest"
              placeholder="RESET"
              data-testid="reset-confirm"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3">
          <Button
            onClick={submit}
            disabled={busy}
            className={`rounded-none text-white ${btnTone[current.tone]}`}
            data-testid="reset-submit"
          >
            <TrashIcon size={14} className="mr-2" />
            {busy ? "Wiping…" : `Permanently delete — ${current.title}`}
          </Button>
        </div>
      </div>

      <BranchesResetPanel />

      {lastResult && (
        <div className="border border-emerald-600 bg-emerald-50 p-4" data-testid="reset-result">
          <div className="overline text-emerald-700">Last reset · {lastResult.scope}</div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs font-mono-num">
            {Object.entries(lastResult.deleted || {}).map(([k, v]) => (
              <div key={k} className="border border-emerald-200 bg-white px-2 py-1 flex justify-between">
                <span className="text-slate-600">{k}</span>
                <span className="font-semibold text-emerald-700">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BranchesResetPanel() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!password) return toast.error("Enter your owner password.");
    if (confirm.trim().toUpperCase() !== "RESET") return toast.error('Type "RESET" to confirm.');
    if (!window.confirm(
      "This will DELETE every branch and detach all users & data from any branch.\n\n" +
      "You will then re-create branches from scratch with fully isolated data.\n\n" +
      "Proceed?"
    )) return;

    try {
      setBusy(true);
      const r = await api.post("/admin/branches/reset", { password, confirm: "RESET" });
      setResult(r.data);
      setPassword(""); setConfirm("");
      toast.success(`Deleted ${r.data.deleted_branches} branch(es).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-[#FF2400] bg-red-50 p-5 space-y-4" data-testid="branches-reset-panel">
      <div className="flex items-center gap-2">
        <BuildingsIcon size={18} weight="fill" className="text-[#FF2400]" />
        <div>
          <div className="font-semibold text-sm">Delete ALL branches & rebuild fresh</div>
          <div className="text-xs text-slate-600 mt-1 max-w-2xl">
            Removes every branch and detaches every user and every record from any branch.
            Existing transactional data stays but becomes "no-branch" (visible only to HQ / no-branch users).
            You then re-create branches from the Branches page, and every new branch starts with zero stock,
            zero collections, zero deliveries — perfectly isolated.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Owner password</Label>
          <Input type="password" autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="rounded-none bg-white" data-testid="branches-reset-password" />
        </div>
        <div>
          <Label className="overline">Type RESET to confirm</Label>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="rounded-none bg-white font-mono-num tracking-widest"
            placeholder="RESET" data-testid="branches-reset-confirm" />
        </div>
      </div>
      <Button onClick={submit} disabled={busy}
        className="rounded-none text-white bg-[#FF2400] hover:bg-red-700"
        data-testid="branches-reset-submit">
        <TrashIcon size={14} className="mr-2" />
        {busy ? "Wiping branches…" : "Delete all branches"}
      </Button>
      {result && (
        <div className="border border-emerald-600 bg-white p-3 text-xs" data-testid="branches-reset-result">
          <div className="overline text-emerald-700 mb-1">
            Deleted {result.deleted_branches} branch(es). Detached branch_id from:
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 font-mono-num">
            {Object.entries(result.cleared_branch_id || {}).map(([k, v]) => (
              <div key={k} className="border border-slate-200 px-2 py-1 flex justify-between">
                <span className="text-slate-600">{k}</span>
                <span className="font-semibold">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
